import { useCallback, useState } from "react";
import { t } from "@/i18n-js/instance";
import { useKeyboardNavigation } from "@circle-react/hooks/utils/useKeyboardNavigation";
import { useCommunityMemberPickerContext } from "../CommunityMemberPickerContext";
import { RecordTypeLabel } from "../RecordTypeLabel";
import { mapSearchResultsRecords } from "../helpers";
import type { RecordType } from "../types";
import { CommunityMemberResult } from "./CommunityMemberResult";
import { MemberTagResult } from "./MemberTagResult";
import { SpaceGroupResult } from "./SpaceGroupResult";
import { SpaceResult } from "./SpaceResult";

interface SearchResultsProps {
  includeAllResultTypes?: boolean;
  onReset?: () => void;
  onSelect?: (record: RecordType) => void;
  records?: RecordType[];
  selectionModalRef?: React.RefObject<HTMLElement>;
}

export const SearchResults = ({
  records = [],
  onSelect,
  onReset,
  selectionModalRef,
  includeAllResultTypes = false,
}: SearchResultsProps) => {
  const { selectionState } = useCommunityMemberPickerContext();
  const {
    spaceGroups,
    spaces,
    memberTags,
    communityMembers,
    combinedRecords,
    recordCount,
  } = mapSearchResultsRecords(records, includeAllResultTypes, selectionState);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const next = useCallback(
    () =>
      setSelectedIndex(
        selectedIndex !== null
          ? Math.min(selectedIndex + 1, recordCount - 1)
          : 0,
      ),
    [selectedIndex, recordCount],
  );

  const previous = useCallback(() => {
    setSelectedIndex(Math.max(Number(selectedIndex) - 1, 0));
  }, [selectedIndex]);

  const onArrowDown = useCallback(
    (event: KeyboardEvent) => {
      if (recordCount) {
        event.preventDefault();
      }
      next();
    },
    [next, recordCount],
  );

  const onArrowUp = useCallback(
    (event: KeyboardEvent) => {
      if (recordCount) {
        event.preventDefault();
      }
      previous();
    },
    [previous, recordCount],
  );

  const onEnter = useCallback(
    (event: KeyboardEvent) => {
      event.stopPropagation();
      event.preventDefault();
      // NOTE: have to check for null or undefined since 0 is valid
      if (selectedIndex === null || typeof selectedIndex === "undefined") {
        return;
      }
      onSelect?.(combinedRecords[selectedIndex]);
    },
    [onSelect, combinedRecords, selectedIndex],
  );

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      if (recordCount) {
        event.stopPropagation();
      }
      setSelectedIndex(null);
      onReset?.();
    },
    [onReset, recordCount],
  );

  useKeyboardNavigation({
    onArrowDown,
    onArrowUp,
    onEnter,
    onEscape,
    enabled: !!recordCount,
    elementRef: selectionModalRef,
  });

  if (!recordCount) {
    return null;
  }

  return (
    <div className="border-primary bg-primary absolute top-full z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border shadow-md">
      {includeAllResultTypes && !!spaceGroups.length && (
        <RecordTypeLabel>
          {t("community_member_select.record_type_labels.space_groups")}
        </RecordTypeLabel>
      )}
      {spaceGroups.map(record => (
        <SpaceGroupResult
          key={record.id}
          record={record}
          onClick={onSelect}
          isSelected={record?.index === selectedIndex}
        />
      ))}
      {includeAllResultTypes && !!spaces.length && (
        <RecordTypeLabel>
          {t("community_member_select.record_type_labels.spaces")}
        </RecordTypeLabel>
      )}
      {spaces.map(record => (
        <SpaceResult
          key={record.id}
          record={record}
          onClick={onSelect}
          isSelected={record?.index === selectedIndex}
        />
      ))}
      {includeAllResultTypes && !!memberTags.length && (
        <RecordTypeLabel>
          {t("community_member_select.record_type_labels.member_tags")}
        </RecordTypeLabel>
      )}
      {memberTags.map(record => (
        <MemberTagResult
          key={record.id}
          record={record}
          onClick={onSelect}
          isSelected={record?.index === selectedIndex}
        />
      ))}
      {includeAllResultTypes && !!communityMembers.length && (
        <RecordTypeLabel>
          {t("community_member_select.record_type_labels.members")}
        </RecordTypeLabel>
      )}
      {communityMembers.map(record => (
        <CommunityMemberResult
          key={record.id}
          record={record}
          onClick={onSelect}
          isSelected={record?.index === selectedIndex}
        />
      ))}
    </div>
  );
};
